import { unsafeCSS, html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { state, customElement } from 'lit/decorators.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';

import { TotalReturnsFragments } from '../../common/fragments/total-returns-fragments';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import type { TotalReturns } from './total-returns.model';
import {
  Constants,
  FREQUENCIES,
  VIEWS,
  YEARS,
} from './total-returns.constants';
import {
  TotalReturnsTableDataType,
  TotalReturnsTableType,
} from '../total-returns-table/total-returns-table.model';
import {
  TotalReturnsChartDataType,
  TotalReturnsChartType,
} from '../total-returns-chart/total-returns-chart.model';
import { dateShort } from '../../common/utils/formatDate';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import {
  hasAnnualizedPerformanceData,
  hasCurrentYearPerformanceData,
} from './total-returns.utils';
import style from './total-returns.scss?inline';

type Frequency = (typeof Constants.FREQUENCIES)[number];
type Years = (typeof Constants.YEARS)[number];
type View = (typeof Constants.VIEWS)[number];

function isFrequency(value: string): value is Frequency {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return (Constants.FREQUENCIES as unknown as string[]).includes(value);
}

function isView(value: string): value is View {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return (Constants.VIEWS as unknown as string[]).includes(value);
}

/**
 * @tag pop-total-returns-component
 * @attribute {TotalReturns} [data-component-object] Total returns content
 */
@customElement(Constants.TAG_NAME)
export class PopTotalReturnsComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  componentObject?: TotalReturns;

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @state()
  frequency: Frequency = Constants.DEFAULT_FREQUENCY;

  /**
   * @internal
   */
  @state()
  years: Years = Constants.DEFAULT_YEARS;

  /**
   * @internal
   */
  @state()
  view: View = Constants.DEFAULT_VIEW;

  fragment = TotalReturnsFragments.TotalReturnsFragment;

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = {
      assetClass: _response.metadata.assetTypeName,
      productName: _response.annualizedSummaryExhibit.productName,
      benchmarkName: _response.annualizedSummaryExhibit.benchmarkName,
      portfolioInceptionDate:
        _response.annualizedSummaryExhibit.portfolioInceptionDate,
      currentYear: {
        monthEnd: _response.currentYearSummaryExhibit.monthEnd,
        disclaimers: _response.currentYearSummaryExhibit.disclaimers,
      },
      annualized: {
        monthEnd: _response.annualizedSummaryExhibit.monthEnd,
        quarterEnd: _response.annualizedSummaryExhibit.quarterEnd,
        disclaimers: _response.annualizedSummaryExhibit.disclaimers,
      },
    };
  }

  private frequencyChangeHandler(
    event: Event & { target?: { value?: string } },
  ) {
    const value = event.target?.value;
    if (value && isFrequency(value)) {
      this.frequency = value;
    } else {
      console.warn(`Invalid frequency value: ${value}`);
    }
  }

  private viewChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value;
    if (value && isView(value)) {
      this.view = value;
    } else {
      console.warn(`Invalid view value: ${value}`);
    }
  }

  private getEffectiveDate() {
    if (this.years === YEARS.CURRENT) {
      return this.componentObject?.currentYear?.monthEnd?.effectiveDate;
    } else {
      if (this.frequency === FREQUENCIES.MONTH) {
        return this.componentObject?.annualized?.monthEnd?.effectiveDate;
      }

      return this.componentObject?.annualized?.quarterEnd?.effectiveDate;
    }
  }

  private renderHeading() {
    return html`
      <h3
        class="b-typography-serif--headline--100 b-font-weight-bold total-returns-heading"
      >
        Total returns
      </h3>
    `;
  }

  private renderEffectiveDate() {
    const effectiveDate = this.getEffectiveDate();

    if (effectiveDate) {
      return html`<p
        class="effective-date b-typography-sans-serif--caption-loose--300 b-font-weight-light"
      >
        ${this.componentObject?.productName} ${Constants.AS_OF_DATE_PREFIX}
        ${dateShort(effectiveDate)}
      </p>`;
    }

    return html``;
  }

  private renderFrequencyDropdown() {
    return html`<beacon-control>
      <label slot="label" for="frequency-select">${Constants.END_DATE}</label>
      <select id="frequency-select" @change="${this.frequencyChangeHandler}">
        <option
          value="${FREQUENCIES.MONTH}"
          ?selected=${this.frequency === FREQUENCIES.MONTH}
        >
          ${Constants.MONTH_END}
        </option>
        <option
          value="${FREQUENCIES.QUARTER}"
          ?selected=${this.frequency === FREQUENCIES.QUARTER}
        >
          ${Constants.QUARTER_END}
        </option>
      </select>
    </beacon-control>`;
  }

  private renderViewDropdown() {
    return html`<beacon-control>
      <label slot="label" for="view-select">${Constants.VIEW}</label>
      <select id="view-select" @change="${this.viewChangeHandler}">
        <option value="${VIEWS.TABLE}" ?selected=${this.view === VIEWS.TABLE}>
          ${Constants.TABLE}
        </option>
        <option value="${VIEWS.CHART}" ?selected=${this.view === VIEWS.CHART}>
          ${Constants.CHART}
        </option>
      </select>
    </beacon-control>`;
  }

  private renderAnnualizedChart() {
    const chartDataType: TotalReturnsChartDataType =
      this.frequency === FREQUENCIES.MONTH
        ? TotalReturnsChartDataType.Monthly
        : TotalReturnsChartDataType.Quarterly;

    return html`<pop-total-returns-chart-component
      .componentObject=${{
        dataValueType: chartDataType,
        chartType: TotalReturnsChartType.AnnualizedPerformance,
        annualizedPerformanceValues: this.componentObject?.annualized,
        portfolioInceptionDate: this.componentObject?.portfolioInceptionDate,
        assetClass: this.componentObject?.assetClass,
      }}
    ></pop-total-returns-chart-component>`;
  }

  private renderCurrentYearChart() {
    return html`<pop-total-returns-chart-component
      .componentObject=${{
        dataValueType: TotalReturnsChartDataType.Quarterly,
        chartType: TotalReturnsChartType.CurrentYearPerformance,
        currentYearPerformanceValues: this.componentObject?.currentYear,
        portfolioInceptionDate: this.componentObject?.portfolioInceptionDate,
        assetClass: this.componentObject?.assetClass,
      }}
    ></pop-total-returns-chart-component>`;
  }

  private renderAnnualizedTable() {
    const tableDataType: TotalReturnsTableDataType =
      this.frequency === FREQUENCIES.MONTH
        ? TotalReturnsTableDataType.Monthly
        : TotalReturnsTableDataType.Quarterly;
    return html`
      <pop-total-returns-table-component
        .componentObject=${{
          dataValueType: tableDataType,
          tableType: TotalReturnsTableType.AnnualizedPerformance,
          annualizedPerformanceValues: this.componentObject?.annualized,
          portfolioInceptionDate: this.componentObject?.portfolioInceptionDate,
          assetClass: this.componentObject?.assetClass,
        }}
      ></pop-total-returns-table-component>
    `;
  }

  private renderCurrentYearTable() {
    return html`<pop-total-returns-table-component
      .componentObject=${{
        dataValueType: TotalReturnsChartDataType.Quarterly,
        tableType: TotalReturnsChartType.CurrentYearPerformance,
        currentYearPerformanceValues: this.componentObject?.currentYear,
        portfolioInceptionDate: this.componentObject?.portfolioInceptionDate,
        assetClass: this.componentObject?.assetClass,
      }}
    ></pop-total-returns-table-component>`;
  }

  private renderBenchmark() {
    if (this.componentObject?.benchmarkName) {
      return html` <p class="benchmark">
        <span
          class="b-typography-sans-serif--caption-loose--300 b-font-weight-bold"
          >Benchmark:</span
        >
        <span
          class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
          >${this.componentObject?.benchmarkName}</span
        >
      </p>`;
    }

    return html``;
  }

  private renderAnnualizedVisual() {
    return this.view === VIEWS.CHART
      ? html`<div
          class="chart-container"
          data-test-id="annualized-total-returns-chart"
        >
          ${this.renderAnnualizedChart()}
        </div>`
      : html`<div
          class="table-container"
          data-test-id="annualized-total-returns-table"
        >
          ${this.renderAnnualizedTable()}
        </div>`;
  }

  private renderCurrentYearVisual() {
    return this.view === VIEWS.CHART
      ? html`<div
          class="chart-container"
          data-test-id="current-year-total-returns-chart"
        >
          ${this.renderCurrentYearChart()}
        </div>`
      : html`<div
          class="table-container"
          data-test-id="current-year-total-returns-table"
        >
          ${this.renderCurrentYearTable()}
        </div>`;
  }

  private renderDisclosures() {
    if (
      this.years === YEARS.MULTI &&
      this.componentObject?.annualized?.disclaimers &&
      this.componentObject?.annualized.disclaimers.length > 0
    ) {
      const disclaimerText = generateLocalDisclaimerText(
        this.componentObject?.annualized.disclaimers,
      );
      return html` <section class="b-margin-block-start--micro--jumbo">
        <pop-disclosure-component data-footnote-indicator-type="symbol">
          ${unsafeHTML(sanitizeHTML(disclaimerText))}
        </pop-disclosure-component>
      </section>`;
    } else if (
      this.years === YEARS.CURRENT &&
      this.componentObject?.currentYear?.disclaimers &&
      this.componentObject?.currentYear.disclaimers.length > 0
    ) {
      const disclaimerText = generateLocalDisclaimerText(
        this.componentObject?.currentYear.disclaimers,
      );
      return html` <section class="b-margin-block-start--micro--jumbo">
        <pop-disclosure-component data-footnote-indicator-type="symbol">
          ${unsafeHTML(sanitizeHTML(disclaimerText))}
        </pop-disclosure-component>
      </section>`;
    }
    return html``;
  }

  private renderAnnualizedTableAndBenchmark() {
    return html` ${this.renderAnnualizedVisual()} ${this.renderBenchmark()} `;
  }
  private renderCurrentYearTableAndBenchmark() {
    return html` ${this.renderCurrentYearVisual()} ${this.renderBenchmark()} `;
  }

  private renderAnnualizedContent() {
    if (
      (this.frequency === FREQUENCIES.MONTH &&
        hasAnnualizedPerformanceData({
          monthEndPerformanceData: this.componentObject?.annualized?.monthEnd,
        })) ||
      (this.frequency === FREQUENCIES.QUARTER &&
        hasAnnualizedPerformanceData({
          quarterEndPerformanceData:
            this.componentObject?.annualized?.quarterEnd,
        }))
    ) {
      return this.renderAnnualizedTableAndBenchmark();
    }

    return html`<pop-no-data-available-component
      .message="${Constants.ANNUALIZED_PERFORMANCE_UNAVAILABLE}"
    ></pop-no-data-available-component>`;
  }

  private renderCurrentYearContent() {
    if (
      hasCurrentYearPerformanceData(this.componentObject?.currentYear?.monthEnd)
    ) {
      return this.renderCurrentYearTableAndBenchmark();
    }

    return html`<pop-no-data-available-component
      .message="${Constants.CURRENT_YEAR_PERFORMANCE_UNAVAILABLE}"
    ></pop-no-data-available-component>`;
  }

  private renderTabs() {
    return html` <beacon-tabs variant="secondary" tab-type="standard">
      <beacon-tab
        slot="tab-children"
        label="Annualized"
        value="1"
        active
        @beacon-event-click="${() => {
          this.years = YEARS.MULTI;
        }}"
      ></beacon-tab>
      <beacon-tab
        slot="tab-children"
        label="Current year"
        value="2"
        @beacon-event-click="${() => {
          this.years = YEARS.CURRENT;
        }}"
      ></beacon-tab>
      <beacon-tab-panel slot="tab-panel" value="1">
        <!--
           The condition checked here is being used as a workaround to ensure
           that the chart renders correctly. Without this, the chart may be
           sized incorrectly due to some interaction between the tabs and the
           chart.
       -->
        ${this.years === YEARS.MULTI
          ? html`<div class="tab-content-top-section">
                <div data-test-id="annualized-effective-date">
                  ${this.renderEffectiveDate()}
                </div>
                <div class="controls-container">
                  ${this.renderFrequencyDropdown()}${this.renderViewDropdown()}
                </div>
              </div>
              ${this.renderAnnualizedContent()} ${this.renderDisclosures()}`
          : html``}
      </beacon-tab-panel>
      <beacon-tab-panel slot="tab-panel" value="2">
        <!--
             The condition checked here is being used as a workaround to ensure
             that the chart renders correctly. Without this, the chart may be
             sized incorrectly due to some interaction between the tabs and the
             chart.
         -->
        ${this.years === YEARS.CURRENT
          ? html` <div class="tab-content-top-section">
                <div data-test-id="current-year-effective-date">
                  ${this.renderEffectiveDate()}
                </div>
                <div class="controls-container">
                  ${this.renderViewDropdown()}
                </div>
              </div>
              ${this.renderCurrentYearContent()} ${this.renderDisclosures()}`
          : html``}
      </beacon-tab-panel>
    </beacon-tabs>`;
  }

  render() {
    if (
      !hasAnnualizedPerformanceData({
        monthEndPerformanceData: this.componentObject?.annualized?.monthEnd,
        quarterEndPerformanceData: this.componentObject?.annualized?.quarterEnd,
      }) &&
      !hasCurrentYearPerformanceData(
        this.componentObject?.currentYear?.monthEnd,
      )
    ) {
      return this.renderComponent(
        html` <section class="b-container unavailable-scenario-container">
          ${this.renderHeading()}
          <pop-no-data-available-component
            .message="${Constants.TOTAL_RETURNS_UNAVAILABLE}"
          ></pop-no-data-available-component>
        </section>`,
      );
    }
    return this.renderComponent(html`
      <section class="b-container">
        ${this.renderHeading()} ${this.renderTabs()}
      </section>
    `);
  }
}
